import { isEqual } from 'lodash-es';
import { createShallowStore, createStoreWithSubscribe } from '@frontend/store';
import { APPOINTMENT_TABS, VIEW_KEYS } from '../constants';
import { AppointmentAnalyticsV2Filters, AppointmentAnalyticsV2TableFiltersState } from '../types';

interface AppointmentAnalyticsV2State {
  defaultFilters: AppointmentAnalyticsV2Filters;
  filterHintText: string;
  filtersInitialized: boolean;
  filters: {
    [APPOINTMENT_TABS.UPCOMING]: AppointmentAnalyticsV2Filters;
    [APPOINTMENT_TABS.PAST]: AppointmentAnalyticsV2Filters;
  };
  tableFilters: {
    [APPOINTMENT_TABS.UPCOMING]: AppointmentAnalyticsV2TableFiltersState;
    [APPOINTMENT_TABS.PAST]: AppointmentAnalyticsV2TableFiltersState;
  };
  activeTab: APPOINTMENT_TABS;
  view: VIEW_KEYS;
}

interface AppointmentAnalyticsV2Store extends AppointmentAnalyticsV2State {
  setDefaultFilters: (defaultFilters: Partial<AppointmentAnalyticsV2Filters>) => void;
  setFilterHintText: (filterHintText: string) => void;
  setFilters: (filters: Partial<AppointmentAnalyticsV2Filters>, tab?: APPOINTMENT_TABS) => void;
  setTableFilters: (filters: Partial<AppointmentAnalyticsV2TableFiltersState>, tab?: APPOINTMENT_TABS) => void;
  setActiveTab: (activeTab: APPOINTMENT_TABS) => void;
  changeView: (view: VIEW_KEYS) => void;
  initialize: (filters: AppointmentAnalyticsV2Filters, upcomingFilters?: AppointmentAnalyticsV2Filters) => void;
  applyFiltersAndSwitchToTableView: (filtersToApply?: Partial<AppointmentAnalyticsV2TableFiltersState>) => void;
}

const getInitialState = (): AppointmentAnalyticsV2State => ({
  defaultFilters: {},
  filterHintText: '',
  filtersInitialized: false,
  filters: {
    [APPOINTMENT_TABS.UPCOMING]: {},
    [APPOINTMENT_TABS.PAST]: {},
  },
  tableFilters: {
    [APPOINTMENT_TABS.UPCOMING]: {},
    [APPOINTMENT_TABS.PAST]: {},
  },
  activeTab: APPOINTMENT_TABS.UPCOMING,
  view: VIEW_KEYS.CHART,
});

const useAppointmentAnalyticsV2Store = createStoreWithSubscribe<AppointmentAnalyticsV2Store>(
  (set, get) => ({
    ...getInitialState(),
    setDefaultFilters: (defaultFilters) => {
      set({ defaultFilters });
    },
    setFilterHintText: (filterHintText) => {
      set({ filterHintText });
    },
    setFilters: (filters, tab) => {
      const { activeTab, filters: currentFilters } = get();
      const targetTab = tab || activeTab;
      set({
        filters: {
          ...currentFilters,
          [targetTab]: {
            ...currentFilters[targetTab],
            ...filters,
          },
        },
      });
    },
    setTableFilters: (filters, tab) => {
      const { activeTab, tableFilters: currentFilters } = get();
      const targetTab = tab || activeTab;
      set({
        tableFilters: {
          ...currentFilters,
          [targetTab]: {
            ...currentFilters[targetTab],
            ...filters,
          },
        },
      });
    },
    setActiveTab: (activeTab) => {
      set({ activeTab });
    },
    changeView: (view: VIEW_KEYS) => {
      const currentState = get();
      set({
        ...currentState,
        view,
      });
    },
    applyFiltersAndSwitchToTableView: (filtersToApply = {}) => {
      const { activeTab, filters: currentFilters, tableFilters } = get();
      set({
        tableFilters: {
          ...tableFilters,
          [activeTab]: {
            ...currentFilters[activeTab],
            ...filtersToApply,
          },
        },
        view: VIEW_KEYS.TABLE,
      });
    },
    initialize: (initialFilters, upcomingFilters = initialFilters) => {
      const { defaultFilters: existingDefaultFilters } = get();

      // Only set the defaults if they haven't been set yet for the current locations.
      // This prevents re-initializing on every render but allows updates when GLS changes.
      if (!isEqual(existingDefaultFilters.locationId?.sort(), initialFilters.locationId?.sort())) {
        set({
          defaultFilters: initialFilters,
          filters: {
            [APPOINTMENT_TABS.UPCOMING]: upcomingFilters,
            [APPOINTMENT_TABS.PAST]: initialFilters,
          },
          tableFilters: {
            [APPOINTMENT_TABS.UPCOMING]: upcomingFilters,
            [APPOINTMENT_TABS.PAST]: initialFilters,
          },
          filtersInitialized: true,
        });
      }
    },
    // setFilters: (filters) => {
    //   const { defaultFilters = {}, filters: currentFilters } = get();
    //   const newFilters: AppointmentAnalyticsTypes.Filters = { ...currentFilters, ...filters };

    //   const { StartDate: defaultStartDate, EndDate: defaultEndDate, ...otherDefaults } = defaultFilters;

    //   const areDatesEqual =
    //     dayjs(newFilters.StartDate).isSame(dayjs(defaultStartDate), 'date') &&
    //     dayjs(newFilters.EndDate).isSame(dayjs(defaultEndDate), 'date');

    //   set({
    //     filters: newFilters,
    //     hasCustomFilters:
    //       !areDatesEqual ||
    //       Object.keys(otherDefaults).some(
    //         (key) =>
    //           !isEqual(
    //             newFilters[key as keyof AppointmentAnalyticsTypes.Filters],
    //             defaultFilters[key as keyof AppointmentAnalyticsTypes.Filters]
    //           )
    //       ),
    //   });
    // },
  }),
  {
    name: 'AppointmentAnalyticsStore',
    trace: true,
  }
);

export const useAppointmentAnalyticsV2ShallowStore =
  createShallowStore<AppointmentAnalyticsV2Store>(useAppointmentAnalyticsV2Store);
